import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { notificationsSelectors } from '@/redux/selectors';
import { notificationsActions } from '@/redux/actions';
import { NotificationItem } from '@/redux/models';
import { NotificationRow } from '@/components/Header/components/NotificationBell/NotificationRow';
import { NotificationModal } from '@/components/Header/components/NotificationBell/NotificationModal';
import { navigateToNotificationLink } from '@/api/amlbotAPI/notifications';

import styles from './Notifications.module.sass';

interface NotificationsProps {}

const Notifications: FC<NotificationsProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const items = useAppSelector(notificationsSelectors.getItems);
  const hasMore = useAppSelector(notificationsSelectors.getHasMore);
  const { nextCursor, unreadCount, loading } = useAppSelector(
    notificationsSelectors.getNotificationsSlice,
  );

  useEffect(() => {
    dispatch(notificationsActions.fetchNotifications());
  }, [dispatch]);

  const [openedItem, setOpenedItem] = useState<NotificationItem | null>(null);

  // Marks read and opens the notification in its popup; an explicit link supplies its
  // own href via `linkHref` and navigates instead — matching the bell dropdown, which
  // renders the same rows. See the note in NotificationList.
  const selectHandler = (item: NotificationItem, linkHref?: string) => {
    if (!item.isRead) {
      dispatch(notificationsActions.markRead({ kind: item.kind, id: item.id }));
    }

    if (linkHref) {
      navigateToNotificationLink(linkHref, navigate);
    } else {
      setOpenedItem(item);
    }
  };

  return (
    <div className={styles.page}>
      {/* No heading here. The app header already renders this page's title from
          navigationPages.NOTIFICATIONS.i18NameKey — the SAME translation key — so a
          heading in the card was the word "Notifications" twice down the screen.
          The bar survives only to carry "mark all as read", so it renders only when
          there is something to mark. */}
      {unreadCount > 0 && (
        <div className={styles.header}>
          <button
            type="button"
            className={styles.markAll}
            onClick={() => dispatch(notificationsActions.markAllRead())}
          >
            {t('translation.notifications.mark_all_read')}
          </button>
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className={styles.empty}>
          {t('translation.notifications.empty')}
        </div>
      )}

      <div className={styles.list}>
        {items.map((item) => (
          <NotificationRow
            key={`${item.kind}-${item.id}`}
            item={item}
            onSelect={selectHandler}
            fullBody
          />
        ))}
      </div>

      {hasMore && (
        <button
          type="button"
          className={styles.loadMore}
          onClick={() =>
            dispatch(
              notificationsActions.fetchNotifications({
                cursor: nextCursor || undefined,
              }),
            )
          }
        >
          {t('translation.notifications.load_more')}
        </button>
      )}

      <NotificationModal
        item={openedItem}
        onNavigate={(href) => navigateToNotificationLink(href, navigate)}
        onClose={() => setOpenedItem(null)}
      />
    </div>
  );
};

export default Notifications;
